import { useState, useEffect } from 'react'

// Sometimes we need to work out if we're explictly in internet explorer 11
export const isIE11 = () => !!window.MSInputMethodContext && !!document.documentMode

// We need to check for whether window is defined before we can use it.
// This is a requirement to make the components Pure and SSR compliant.
export const isClientSide = () => typeof window !== 'undefined'

// This function is not SSR safe, you must call isClientSide before running this.
export const getWidth = () =>
    window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

export const useCurrentWidth = () => {
    // We're probably not client side if window isn't defined. We can just assume a
    // desktop style layout by returning 1920.
    if (!isClientSide()) return 1920
    // Else, lets check window size normally.

    // save current window width in the state object
    let [width, setWidth] = useState(getWidth())

    // in this case useEffect will execute only once because
    // it does not have any dependencies.
    useEffect(() => {
        // timeoutId for debounce mechanism
        let timeoutId = null
        const resizeListener = () => {
            // prevent execution of previous setTimeout
            clearTimeout(timeoutId)
            // change width from the state object after 150 milliseconds
            timeoutId = setTimeout(() => setWidth(getWidth()), 150)
        }
        // set resize listener
        window.addEventListener('resize', resizeListener)

        // clean up function
        return () => {
            // remove resize listener
            window.removeEventListener('resize', resizeListener)
        }
    }, [])

    return width
}

// Add a new set of methods that determine the position of an element ref
export const getElementTopPosition = element =>
    element !== null && element !== undefined ? element.getBoundingClientRect().top : undefined

// Element *MUST* be a useRef element here.
// The rest of the function behaves like the above window resize hook, just with a scroll listener instead.
export const useElementTopPosition = element => {
    if (!isClientSide()) return 0

    const [position, setPosition] = useState(0)

    useEffect(() => {
        let timeoutId = null
        const scrollListener = () => {
            clearTimeout(timeoutId)
            timeoutId = setTimeout(() => setPosition(getElementTopPosition(element.current)), 150) // Current returns the element once it's mounted, otherwise it's null.
        }

        setPosition(getElementTopPosition(element.current))
        window.addEventListener('scroll', scrollListener)

        return () => {
            window.removeEventListener('scroll', scrollListener)
        }
    }, [])

    return position
}
